import { computed, ref, unref, watch } from 'vue';
import axios from 'axios';
import { getApiBaseUrl } from '@/config';
import { useMarketingFilters } from './useMarketingFilters';

function compactParams(params) {
  const out = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return;
    if (Array.isArray(value) && !value.length) return;
    out[key] = Array.isArray(value) ? value.join(',') : value;
  });
  return out;
}

export function useMarketingDashboard(endpoint, extraParams) {
  const { apiParams } = useMarketingFilters();
  const data = ref({});
  const loading = ref(true);
  const error = ref('');
  const loadedOnce = ref(false);
  let abortController = null;
  let requestId = 0;

  const extra = computed(() => {
    if (!extraParams) return {};
    return unref(extraParams) || {};
  });

  async function load() {
    const id = ++requestId;
    if (abortController) abortController.abort();
    abortController = new AbortController();
    loading.value = true;
    error.value = '';
    try {
      const params = compactParams({ ...apiParams.value, ...extra.value });
      const response = await axios.get(`${getApiBaseUrl()}${endpoint}`, {
        params,
        withCredentials: true,
        signal: abortController.signal
      });
      if (id !== requestId) return;
      data.value = response.data || {};
      loadedOnce.value = true;
    } catch (err) {
      if (axios.isCancel(err) || err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
        return;
      }
      if (id !== requestId) return;
      error.value = err.response?.data?.error || 'Marketing-Daten konnten nicht geladen werden.';
    } finally {
      if (id === requestId) loading.value = false;
    }
  }

  watch([apiParams, extra], load, { immediate: true, deep: true });

  return {
    data,
    loading,
    error,
    loadedOnce,
    reload: load,
    showInitialLoading: computed(() => loading.value && !loadedOnce.value)
  };
}
